import { Fragment } from 'react';

import { SupplierGroupMessage } from '../../../../../types/SupplierGroupMessage';
import { Link } from '../../../../cdl/Link/Link';
import { Text } from '../../../../cdl/Text/Text';
import { BooleanStatus } from '../../../../common/BooleanStatus/BooleanStatus';
import { DataSheet } from '../../../../common/DataSheet/DataSheet';
import { formatAddress } from '../../../../common/helpers/formatAddress.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { Characters } from '../../../../common/helpers/unicode.helper';
import { useEnums } from '../../../../common/hooks/useEnums';
import { useRole } from '../../../../common/hooks/useRole';
import { Box } from '../../../../common/ui/Box';

interface SupplierGroupDataSheetProps {
    supplierGroup: SupplierGroupMessage;
}

export const SupplierGroupDataSheet = ({ supplierGroup }: SupplierGroupDataSheetProps) => {
    const { getHumanReadableValue } = useEnums();
    const role = useRole();

    return (
        <Box display="flex" flexDirection="column" gap={40}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={40}>
                <DataSheet>
                    <DataSheet.Label>{translate('navigation.group')}</DataSheet.Label>
                    <Text variant="small">{supplierGroup.name || Characters.EN_DASH}</Text>

                    <DataSheet.Label>{translate('global.email')}</DataSheet.Label>
                    <Text variant="small">{supplierGroup.email || Characters.EN_DASH}</Text>

                    <DataSheet.Label>{translate('global.contact.phone')}</DataSheet.Label>
                    <Text variant="small">{supplierGroup.phone || Characters.EN_DASH}</Text>

                    <DataSheet.Label>{translate('global.contact.fax')}</DataSheet.Label>
                    <Text variant="small">{supplierGroup.fax || Characters.EN_DASH}</Text>

                    <DataSheet.Label>{translate('company.headquarter')}</DataSheet.Label>
                    <Text variant="small">{supplierGroup.headquarter || Characters.EN_DASH}</Text>

                    <DataSheet.Label>{translate('company.description')}</DataSheet.Label>
                    <Text variant="small">{supplierGroup.description || Characters.EN_DASH}</Text>

                    <DataSheet.Label>{translate('suppliergroup.type')}</DataSheet.Label>
                    <Text variant="small">{getHumanReadableValue('supplierGroupType', supplierGroup.type)}</Text>

                    <DataSheet.Label>{translate('suppliergroup.testcompany')}</DataSheet.Label>
                    <BooleanStatus value={supplierGroup.testCompany} />

                    <DataSheet.Label>{translate('company.emailQuoteOnly')}</DataSheet.Label>
                    <BooleanStatus value={supplierGroup.emailQuote} />
                </DataSheet>
                <DataSheet>
                    <DataSheet.Label>{translate('global.address.title')}</DataSheet.Label>
                    <Text variant="small">
                        {supplierGroup.address ? formatAddress({ address: supplierGroup.address }) : Characters.EN_DASH}
                    </Text>

                    <DataSheet.Label>{translate('company.gtc')}</DataSheet.Label>
                    <Link external href={supplierGroup.gtcUrl} target="_blank" size="small">
                        {translate('company.gtcView')}
                    </Link>

                    <DataSheet.Label>{translate('company.gtcAccepted')}</DataSheet.Label>
                    <BooleanStatus
                        value={supplierGroup.gtcAccepted}
                        label={
                            supplierGroup.gtcAccepted
                                ? translate('company.gtcStatusAccepted')
                                : translate('company.gtcNotAccepted')
                        }
                    />
                </DataSheet>
            </Box>

            {role.hasAdminRights() ? (
                <Box display="grid" gridTemplateColumns="1fr" gap={40}>
                    <DataSheet>
                        <DataSheet.Heading>{translate('company.settings')}</DataSheet.Heading>
                        <Fragment>
                            <DataSheet.Label>{translate('company.spotallowed')}</DataSheet.Label>
                            <BooleanStatus value={supplierGroup.lubesSettings.spotAllowed} />
                        </Fragment>
                        <DataSheet.Label>{translate('company.supplierproductgroups')}</DataSheet.Label>
                        <Text variant="small">
                            {supplierGroup.supplierProductGroups?.length
                                ? supplierGroup.supplierProductGroups
                                      .map((productGroup) =>
                                          getHumanReadableValue('supplierProductGroup', productGroup)
                                      )
                                      .join(', ')
                                : Characters.EN_DASH}
                        </Text>

                        <DataSheet.Label>{translate('company.translateproductgroup')}</DataSheet.Label>
                        <Text variant="small">
                            {supplierGroup.translationSupplierProductGroup
                                ? getHumanReadableValue(
                                      'supplierProductGroup',
                                      supplierGroup.translationSupplierProductGroup
                                  )
                                : Characters.EN_DASH}
                        </Text>
                    </DataSheet>
                </Box>
            ) : null}
        </Box>
    );
};
